import { Button, Inline, Skeleton } from '@omnipresentgroup/design-system';
import type { PayrollProvider } from '@omnipresentgroup/types';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import {
  getCountryData,
  getEmojiFlag,
  type TCountryCode,
} from 'countries-list';
import { useMemo } from 'react';

import { ProviderStatusTag } from '../../components';

export enum ColumnHeaders {
  name = 'Name',
  countryCode = 'Country',
  isActive = 'Status',
  actions = '',
}

export const usePayrollProvidersTableConfig = ({
  columnsOverride,
  onEditClick,
}: {
  columnsOverride?: ColumnDef<PayrollProvider, unknown>[];
  onEditClick?: (id: string) => void;
}) => {
  const columnHelper = useMemo(() => createColumnHelper<PayrollProvider>(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        cell: (info) => info.getValue(),
        header: () => ColumnHeaders.name,
      }),
      columnHelper.accessor('countryCode', {
        cell: (info) => {
          const countryCode = info.getValue() as TCountryCode;
          return `${getEmojiFlag(countryCode)} ${getCountryData(countryCode).name}`;
        },
        header: () => ColumnHeaders.countryCode,
        sortingFn: (rowA, rowB) =>
          getCountryData(rowA.original.countryCode as TCountryCode).name >
          getCountryData(rowB.original.countryCode as TCountryCode).name
            ? 1
            : -1,
      }),
      columnHelper.accessor('isActive', {
        cell: (info) => <ProviderStatusTag isActive={info.getValue()} />,
        header: () => ColumnHeaders.isActive,
        size: 60,
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => (
          <Inline gap="8" justify="flex-end">
            <Button
              key={`edit-${info.row.original.id}`}
              variant="tertiary"
              icon="Edit"
              size="small"
              onClick={() => onEditClick?.(info.row.original.id)}
            />
          </Inline>
        ),
        header: () => ColumnHeaders.actions,
        size: 24,
        enableSorting: false,
        meta: {
          onClickStopPropagation: true,
          loader: (
            <Inline gap="8" justify="flex-end">
              <Skeleton height={16} width={16} />
            </Inline>
          ),
        },
      }),
      ...(columnsOverride ?? []),
    ],
    [columnHelper, columnsOverride, onEditClick],
  );

  return { columns, columnHelper };
};
